import { useDispatch, useSelector } from "react-redux";
import {
  clearAuth,
  setCredentials,
  setForbidden,
  setIsAuthenticated,
  setUser,
} from "@/slides/auth/authSlice";
import { AppDispatch, RootState } from "@/stores";
import { GetUserProfile } from "@/dto/auth/response/auth-response";
import RoutesString from "@/pages/routesString";
import { useGetUserProfileQuery } from "@/services/authAPI";
import sessionUtil from "@/utils/sessionUtil";
import { useEffect } from "react";
import { RESULT_CODE } from "@/constants/enum";
import { get } from "lodash";
import databases from "@/cache";
import { storeKey as authStoreKey } from "@/stores/AuthenticationStore/authentication";

const useAuth = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data, isFetching } = useGetUserProfileQuery(undefined, {
    skip: !sessionUtil.hasSession(),
    refetchOnMountOrArgChange: false,
  });

  const code = get(data, "body.resultCode", null);

  const result = get(data, "body", null);

  const user = useSelector((state: RootState) => state.auth.user);
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );
  const isForbidden = useSelector((state: RootState) => state.auth.isForbidden);

  /**
   * #6385: đăng xuất phải xoá HẾT state trong bộ nhớ, không chỉ redux auth.
   *
   * Trước đây dùng `navigate()` — điều hướng SPA nên trang không tải lại, và 13 store
   * `react-sweet-state` (BillPayment, TransferBy*, Saving, Onboarding…) giữ nguyên dữ liệu.
   * Đăng nhập lại là vào thẳng bước dở dang của phiên trước, kèm cả thông tin đã nhập —
   * trên máy dùng chung thì đó là dữ liệu của người trước.
   *
   * Dùng `location.replace` thay vì gọi `clearStore()` của từng store: liệt kê 13 store là
   * cách hỏng âm thầm ngay lần đầu có người thêm luồng mới mà quên bổ sung. Tải lại trang
   * thì store thứ 14 cũng sạch. Đã kiểm: không store nào được dùng ở màn đăng nhập nên
   * không mất gì. `replace` (không phải `href`) để nút Back không quay lại được màn đã đăng nhập.
   *
   * `dispatch` chạy đồng bộ nên `clearAuth()` kịp xoá localStorage trước khi trang tải lại.
   *
   * <p>Tải lại trang dọn sạch BỘ NHỚ, nhưng KHÔNG dọn thứ đã ghi xuống đĩa. Store auth được
   * middleware `persistent` ghi vào IndexedDB, và `withAuthPersist` đọc lại đúng bản đó ở
   * {@code useLayoutEffect} mỗi lần app khởi động — nên hồ sơ người dùng cũ sống lại ngay sau khi
   * tải lại. Chỗ duy nhất gọi `removeItem` là `actionAuth.logout()`, đang bị comment ở cả ba nơi
   * gọi. Phải xoá TRƯỚC khi điều hướng, nếu không trang tải lại sẽ cắt ngang lời gọi bất đồng bộ.
   */
  const clearAuthState = async () => {
    dispatch(clearAuth());
    try {
      await databases.removeItem(authStoreKey);
    } catch (error) {
      // Xoá hụt thì vẫn phải đăng xuất — không giữ người dùng lại trong phiên cũ.
      // eslint-disable-next-line no-console
      console.error("clearAuthState: khong xoa duoc ban auth da luu", error);
    }
    window.location.replace(RoutesString.Login);
  };

  const updateCredentials = (userReq: GetUserProfile, sessionId: string) => {
    dispatch(setCredentials({ user: userReq, sessionId }));
  };

  const updateImage = (userReq: GetUserProfile) => {
    dispatch(setUser({ user: userReq }));
  };

  const dispatchSetUser = (request: GetUserProfile | null) => {
    if (request) {
      dispatch(
        setUser({
          user: request,
        })
      );
    }
  };

  const dispatchSetIsAuthenticated = (value: boolean) =>
    dispatch(setIsAuthenticated(value));
  const dispatchSetForbidden = (value: boolean) =>
    dispatch(setForbidden(value));

  const updateIsAuthenticated = (status: boolean) => {
    dispatch(setIsAuthenticated(status));
  };

  const updateForbidden = (status: boolean) => {
    dispatch(setForbidden(status));
  };

  useEffect(() => {
    if (data) {
      switch (code) {
        case RESULT_CODE.SUCCESS:
          if (result) {
            dispatchSetUser(result);
            dispatchSetIsAuthenticated(true);
            dispatchSetForbidden(false);
          }
          break;
        // case API_CODE.FORBIDDEN:
        //   dispatchSetForbidden(true);
        //   break;
        default:
          // clearAuthState();
          break;
      }
    }
  }, [data]);

  return {
    data,
    isFetching,
    user,
    isAuthenticated,
    isForbidden,
    clearAuthState,
    updateCredentials,
    updateIsAuthenticated,
    updateForbidden,
    updateImage,
  };
};

export default useAuth;
